import React from 'react';
import styled from '@emotion/styled';

// ── Types ─────────────────────────────────────────────────────────────────────

type Variant = 'default' | 'danger' | 'accent';

interface TextButtonProps {
  onClick?: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  variant?: Variant;
  /** Accessible label when button text alone is insufficient */
  'aria-label'?: string;
}

// ── Styled atom ───────────────────────────────────────────────────────────────

const Root = styled.button<{ $variant: Variant }>`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  transition:
    opacity var(--duration-shortest),
    color var(--duration-shortest);
  color: ${({ $variant }) =>
    $variant === 'danger'
      ? 'var(--color-error)'
      : $variant === 'accent'
        ? 'var(--color-accent)'
        : 'var(--color-text-secondary)'};

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * TextButton — a minimal inline text-only button for secondary actions.
 *
 * Use when a full Button would be visually heavy, e.g. "Cancel", "Forgot password?",
 * "Show more". Supports `default`, `danger`, and `accent` colour variants.
 *
 * @example
 * <TextButton variant="danger" onClick={handleDelete}>Delete account</TextButton>
 */
const TextButton: React.FC<TextButtonProps> = ({
  onClick,
  children,
  disabled,
  className,
  variant = 'default',
  'aria-label': ariaLabel,
}) => (
  <Root
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={className}
    $variant={variant}
    aria-label={ariaLabel}
  >
    {children}
  </Root>
);

export default TextButton;
